import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://dockclaw.com';

// ---------------------------------------------------------------------------
// Factory helpers – each returns a plain JSON-LD-ready object
// ---------------------------------------------------------------------------

/** DockClaw organisation schema */
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DockClaw',
    url: BASE_URL,
    logo: `${BASE_URL}/logo-200.png`,
    sameAs: [
      'https://twitter.com/dockclaw',
      'https://github.com/dockclaw',
    ],
    description:
      'DockClaw — managed hosting platform for OpenClaw AI agents on Telegram.',
  };
}

/**
 * FAQPage schema.
 * @param {{ question: string, answer: string }[]} items
 */
export function faqSchema(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };
}

/**
 * HowTo schema.
 * @param {string} name
 * @param {{ name: string, text: string }[]} steps
 */
export function howToSchema(name, steps = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    step: steps.map(({ name: stepName, text }, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: stepName,
      text,
    })),
  };
}

/**
 * BreadcrumbList schema.
 * @param {{ name: string, url: string }[]} items
 */
export function breadcrumbSchema(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(({ name, url }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: url,
    })),
  };
}

/**
 * Article schema.
 * @param {{ title: string, description: string, datePublished: string, dateModified?: string, author?: string }} opts
 */
export function articleSchema({
  title,
  description,
  datePublished,
  dateModified,
  author = 'DockClaw Team',
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'DockClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo-200.png`,
      },
    },
  };
}

/**
 * Product schema.
 * @param {{ name: string, description: string, price?: string, currency?: string }} opts
 */
export function productSchema({
  name,
  description,
  price = '19.99',
  currency = 'USD',
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: 'https://dockclaw.com/icon-512x512.png',
    brand: {
      '@type': 'Organization',
      name: 'DockClaw',
    },
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
      url: 'https://dockclaw.com/pricing',
    },
  };
}

// ---------------------------------------------------------------------------
// Component – renders one or more JSON-LD blocks inside <Helmet>
// ---------------------------------------------------------------------------

/**
 * SchemaMarkup — inject arbitrary JSON-LD into <head>.
 *
 * @param {{ schema: object | object[] }} props
 */
export default function SchemaMarkup({ schema }) {
  if (!schema) return null;

  const schemas = Array.isArray(schema) ? schema : [schema];

  return (
    <Helmet>
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s, null, 2)}
        </script>
      ))}
    </Helmet>
  );
}
